import { Box, Button, Flex, Input, Text } from '@chakra-ui/react';
import Info from '@ui/components/Info';
import { useStrategySlippage } from '@ui/hooks/useStrategySettings';
import { useTranslations } from '@ui/i18n';
import { MAXW_DESKTOP_CTA } from '@ui/utils/constants';
import { formatPercentage } from '@ui/utils/format';
import { ColorThemeVars, InFerro } from '@veno-app/chakra';
import { Close } from '@veno-app/icons';
import React, { useState } from 'react';

function StrategySlippage() {
  const t = useTranslations();
  const [slippageEditorOpen, setSlippageEditorOpen] = useState(false);
  const { slippage: initialSlippage, setSlippage } = useStrategySlippage();
  const [value, setValue] = useState(`${(initialSlippage / 100).toFixed(1)}`);
  const [errorMessage, setErrorMessage] = useState('');

  const onChange = (value: string) => {
    setErrorMessage('');
    if (parseFloat(value) <= 0) setErrorMessage(t('invalid format'));
    if (isNaN(parseFloat(value))) setErrorMessage(t('invalid format'));
    if (new RegExp(`\\.[0-9]{${2},}$`).test(value)) {
      return;
    }
    if (parseFloat(value) > 99.9 || parseFloat(value) < 0) return;
    setValue(value);
  };

  return (
    <InFerro>
      {!slippageEditorOpen && (
        <Info
          title={t('Slippage Tolerance')}
          value={
            initialSlippage !== undefined ? (
              <Flex gap={2} flexDir="column" align="flex-end">
                {formatPercentage(initialSlippage / 10000, 1)}{' '}
                <Button
                  justifyContent="flex-end"
                  variant="link"
                  color="pink.500"
                  onClick={() => setSlippageEditorOpen(true)}
                  cursor="pointer"
                >
                  {t('Edit')}
                </Button>
              </Flex>
            ) : undefined
          }
          mt="16px"
        />
      )}
      {slippageEditorOpen && (
        <Box
          background={`linear-gradient(to right, #009BE0, #FFFFFF, #E9540E)`}
          borderRadius="10px"
          padding="1px" // This creates the "border" effect with the background gradient
          mt="16px"
        >
          <Flex
            flexDir="column"
            borderRadius="10px"
            bg={`var(${ColorThemeVars.mainColor})`}
            p={6}
          >
            <Flex w="full" justify="space-between">
              <Text mb={2} textStyle="body">
                {t('Slippage Tolerance')}
              </Text>
              <Close
                cursor="pointer"
                onClick={() => setSlippageEditorOpen(false)}
              />
            </Flex>
            <Input
              type="number"
              min="0"
              mb={4}
              rounded="10px"
              border={'1px solid #505050'}
              value={value}
              onWheel={(e) => (e.target as HTMLElement).blur()}
              p={4}
              w="full"
              variant="unstyled"
              onChange={(e) => {
                onChange(e.target.value);
              }}
            />
            <Flex w="full" mb={3} cursor="pointer">
              <Box
                background={
                  value === '0.5'
                    ? `linear-gradient(to right, #009BE0, #FFFFFF, #E9540E)`
                    : '#505050'
                }
                roundedLeft="10px"
                padding="1px"
                flex="1"
              >
                <Flex
                  onClick={() => onChange('0.5')}
                  roundedLeft="10px"
                  bgColor="#000000"
                  py={2}
                  px={1}
                  justify="center"
                  align="center"
                >
                  0.5 %
                </Flex>
              </Box>
              <Box
                background={
                  value === '1' || value === '1.0'
                    ? `linear-gradient(to right, #009BE0, #FFFFFF, #E9540E)`
                    : '#505050'
                }
                padding="1px"
                flex="1"
              >
                <Flex
                  onClick={() => onChange('1.0')}
                  bgColor="#000000"
                  py={2}
                  px={1}
                  justify="center"
                  align="center"
                >
                  1.0 %
                </Flex>
              </Box>
              <Box
                background={
                  value === '1.5'
                    ? `linear-gradient(to right, #009BE0, #FFFFFF, #E9540E)`
                    : '#505050'
                }
                padding="1px"
                flex="1"
              >
                <Flex
                  onClick={() => onChange('1.5')}
                  bgColor="#000000"
                  py={2}
                  px={1}
                  justify="center"
                  align="center"
                >
                  1.5 %
                </Flex>
              </Box>
              <Box
                roundedRight="10px"
                background={
                  value === '2' || value === '2.0'
                    ? `linear-gradient(to right, #009BE0, #FFFFFF, #E9540E)`
                    : '#505050'
                }
                padding="1px"
                flex="1"
              >
                <Flex
                  onClick={() => onChange('2.0')}
                  roundedRight="10px"
                  bgColor="#000000"
                  py={2}
                  px={1}
                  justify="center"
                  align="center"
                >
                  2 %
                </Flex>
              </Box>
            </Flex>
            <Button
              mx="auto"
              w="full"
              maxW={MAXW_DESKTOP_CTA}
              disabled={!!errorMessage || !value}
              onClick={() => {
                setSlippage(parseFloat(value) * 100);
                setSlippageEditorOpen(false);
              }}
            >
              {t('Confirm')}
            </Button>
          </Flex>
        </Box>
      )}
    </InFerro>
  );
}

export default StrategySlippage;
